import { useMemo, useState } from 'react';
import { Button, Form, Modal, ProgressBar } from 'react-bootstrap';
import { GENERAL_CATEGORY_OPTIONS } from '../constants/categories';
import { useStaffTypes } from '../hooks/useStaffTypes';

const KEEP = '__keep__';

const COST_FIELDS = [
    { key: 'catering', label: 'Catering' },
    { key: 'cleaning', label: 'Cleaning' },
    { key: 'laundry', label: 'Laundry' },
    { key: 'other', label: 'Other' },
];

const INITIAL_FIELDS = {
    sites: { enabled: false, value: [] },
    customer_care_type_id: { enabled: false, value: [] },
    frequency: { enabled: false, value: '' },
    sn_hours_day: { enabled: false, value: '' },
    sn_hours_night: { enabled: false, value: '' },
    ena_hours_day: { enabled: false, value: '' },
    ena_hours_night: { enabled: false, value: '' },
    cw_hours_day: { enabled: false, value: '' },
    cw_hours_night: { enabled: false, value: '' },
    catering: { enabled: false, value: '' },
    cleaning: { enabled: false, value: '' },
    laundry: { enabled: false, value: '' },
    other: { enabled: false, value: '' },
    date_from: { enabled: false, value: '' },
    date_to: { enabled: false, value: '' },
};

function BulkEditPriceItemsModal({ show, onHide, selectedCount, customerCareTypes, availableSites = [], onApply, isApplying, progress }) {
    const { getAbbreviation } = useStaffTypes();
    const minuteFields = useMemo(
        () => [
            { key: 'sn_hours_day', label: `${getAbbreviation('SN')} Minutes (Day)` },
            { key: 'sn_hours_night', label: `${getAbbreviation('SN')} Minutes (Night)` },
            { key: 'ena_hours_day', label: `${getAbbreviation('ENA')} Minutes (Day)` },
            { key: 'ena_hours_night', label: `${getAbbreviation('ENA')} Minutes (Night)` },
            { key: 'cw_hours_day', label: `${getAbbreviation('CW')} Minutes (Day)` },
            { key: 'cw_hours_night', label: `${getAbbreviation('CW')} Minutes (Night)` },
        ],
        [getAbbreviation]
    );
    const allFieldDefs = useMemo(
        () => [
            { key: 'sites', label: 'Sites', section: 'Sites' },
            { key: 'general_category', label: 'General Category', section: 'Classification' },
            { key: 'customer_care_type_id', label: 'Default on Quote', section: 'Classification' },
            { key: 'frequency', label: 'Frequency', section: 'Classification' },
            ...minuteFields.map((f) => ({ ...f, section: 'Minutes' })),
            ...COST_FIELDS.map((f) => ({ ...f, section: 'Additional Costs' })),
            { key: 'date_from', label: 'Date From', section: 'Period' },
            { key: 'date_to', label: 'Date To', section: 'Period' },
        ],
        [minuteFields]
    );

    const [fieldSearch, setFieldSearch] = useState('');
    const [generalCategory, setGeneralCategory] = useState(KEEP);
    const [fields, setFields] = useState(INITIAL_FIELDS);

    const setEnabled = (key, enabled) =>
        setFields(prev => ({ ...prev, [key]: { ...prev[key], enabled } }));

    const setFieldValue = (key, value) =>
        setFields(prev => ({ ...prev, [key]: { ...prev[key], value } }));

    const toggleSite = (siteId) => {
        setFields(prev => {
            const current = prev.sites.value;
            const site = availableSites.find(s => s.site_id === siteId);
            const next = current.some(s => s.site_id === siteId)
                ? current.filter(s => s.site_id !== siteId)
                : site ? [...current, site] : current;
            return { ...prev, sites: { ...prev.sites, value: next } };
        });
    };

    const toggleCareType = (id) => {
        setFields(prev => {
            const current = prev.customer_care_type_id.value;
            const next = current.includes(id) ? current.filter(x => x !== id) : [...current, id];
            return { ...prev, customer_care_type_id: { ...prev.customer_care_type_id, value: next } };
        });
    };

    const resetFields = () => {
        setFieldSearch('');
        setGeneralCategory(KEEP);
        setFields(INITIAL_FIELDS);
    };

    const handleClose = () => {
        if (isApplying) return;
        resetFields();
        onHide();
    };

    const handleApply = () => {
        if (fields.sites.enabled && fields.sites.value.length === 0) {
            alert('Please select at least one site, or uncheck the Sites override.');
            return;
        }
        const changes = {};
        if (generalCategory !== KEEP) changes.general_category = generalCategory;
        Object.entries(fields).forEach(([key, { enabled, value }]) => {
            if (!enabled) return;
            if (key === 'customer_care_type_id') {
                changes[key] = value.length > 0 ? value.join(', ') : null;
            } else if (key === 'sites') {
                changes[key] = value;
            } else {
                changes[key] = value;
            }
        });
        onApply(changes);
    };

    const searchLower = fieldSearch.trim().toLowerCase();
    const visible = (key) => {
        if (!searchLower) return true;
        const def = allFieldDefs.find(f => f.key === key);
        return def ? def.label.toLowerCase().includes(searchLower) : false;
    };

    const progressPercent = progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0;

    const sectionHeader = (label) => (
        <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--theme-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', padding: '8px 12px 4px', backgroundColor: 'var(--theme-surface-muted)', borderBottom: '1px solid #dee2e6' }}>
            {label}
        </div>
    );

    const numericRow = (key, label) => visible(key) && (
        <div key={key} style={rowStyle}>
            <span style={labelStyle}>{label}</span>
            <div style={{ ...controlStyle, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Form.Check
                    type="checkbox"
                    checked={fields[key].enabled}
                    onChange={e => setEnabled(key, e.target.checked)}
                    style={{ margin: 0 }}
                />
                <Form.Control
                    type="number"
                    size="sm"
                    placeholder="Keep as is"
                    value={fields[key].value}
                    onChange={e => setFieldValue(key, e.target.value)}
                    disabled={!fields[key].enabled}
                    min={0}
                    style={{ flex: 1 }}
                />
            </div>
        </div>
    );

    const sitesVisible = visible('sites');
    const classificationVisible = visible('general_category') || visible('customer_care_type_id') || visible('frequency');
    const minutesVisible = minuteFields.some(f => visible(f.key));
    const costsVisible = COST_FIELDS.some(f => visible(f.key));
    const periodVisible = visible('date_from') || visible('date_to');

    return (
        <Modal show={show} onHide={handleClose} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title style={{ fontSize: '1.1rem' }}>
                    Bulk edit &nbsp;<span style={{ color: 'var(--theme-text-secondary)', fontWeight: 400 }}>|</span>&nbsp; {selectedCount} price item{selectedCount !== 1 ? 's' : ''}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ padding: 0 }}>
                <div style={{ padding: '12px 16px', borderBottom: '1px solid #dee2e6' }}>
                    <p className="text-muted mb-2" style={{ fontSize: '0.9rem' }}>Modify any of the below fields to continue.</p>
                    <Form.Control
                        type="text"
                        placeholder="Search for fields..."
                        value={fieldSearch}
                        onChange={e => setFieldSearch(e.target.value)}
                        size="sm"
                    />
                </div>

                <div style={{ maxHeight: '55vh', overflowY: 'auto', border: '1px solid #dee2e6', borderTop: 'none' }}>

                    {/* Sites */}
                    {sitesVisible && sectionHeader('Sites')}
                    {sitesVisible && (
                        <div style={{ ...rowStyle, alignItems: 'flex-start' }}>
                            <span style={labelStyle}>Sites</span>
                            <div style={controlStyle}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: fields.sites.enabled ? '8px' : 0 }}>
                                    <Form.Check
                                        type="checkbox"
                                        checked={fields.sites.enabled}
                                        onChange={e => setEnabled('sites', e.target.checked)}
                                        label="Override"
                                        style={{ margin: 0 }}
                                    />
                                </div>
                                {fields.sites.enabled && (
                                    <div style={{ maxHeight: '120px', overflowY: 'auto', border: '1px solid #ced4da', borderRadius: '4px', padding: '6px' }}>
                                        {availableSites.length === 0 && (
                                            <span style={{ fontSize: '0.85rem', color: 'var(--theme-text-secondary)' }}>No sites available</span>
                                        )}
                                        {availableSites.map(site => (
                                            <Form.Check
                                                key={site.site_id}
                                                type="checkbox"
                                                label={site.site_name}
                                                checked={fields.sites.value.some(s => s.site_id === site.site_id)}
                                                onChange={() => toggleSite(site.site_id)}
                                                style={{ fontSize: '0.875rem' }}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {classificationVisible && sectionHeader('Classification')}

                    {visible('general_category') && (
                        <div style={rowStyle}>
                            <span style={labelStyle}>General Category</span>
                            <Form.Control as="select" size="sm" value={generalCategory} onChange={e => setGeneralCategory(e.target.value)} style={controlStyle}>
                                <option value={KEEP}>Keep as is</option>
                                {GENERAL_CATEGORY_OPTIONS.map(cat => (
                                    <option key={cat} value={cat}>{cat}</option>
                                ))}
                            </Form.Control>
                        </div>
                    )}

                    {visible('customer_care_type_id') && (
                        <div style={{ ...rowStyle, alignItems: 'flex-start' }}>
                            <span style={labelStyle}>Default on Quote</span>
                            <div style={controlStyle}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: fields.customer_care_type_id.enabled ? '8px' : 0 }}>
                                    <Form.Check
                                        type="checkbox"
                                        checked={fields.customer_care_type_id.enabled}
                                        onChange={e => setEnabled('customer_care_type_id', e.target.checked)}
                                        label="Override"
                                        style={{ margin: 0 }}
                                    />
                                </div>
                                {fields.customer_care_type_id.enabled && (
                                    <div style={{ maxHeight: '120px', overflowY: 'auto', border: '1px solid #ced4da', borderRadius: '4px', padding: '6px' }}>
                                        {customerCareTypes.length === 0 && (
                                            <span style={{ fontSize: '0.85rem', color: 'var(--theme-text-secondary)' }}>No care types available</span>
                                        )}
                                        {customerCareTypes.map(ct => (
                                            <Form.Check
                                                key={ct.id}
                                                type="checkbox"
                                                label={ct.name}
                                                checked={fields.customer_care_type_id.value.includes(ct.id.toString())}
                                                onChange={() => toggleCareType(ct.id.toString())}
                                                style={{ fontSize: '0.875rem' }}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {visible('frequency') && (
                        <div style={rowStyle}>
                            <span style={labelStyle}>Frequency</span>
                            <div style={{ ...controlStyle, display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Form.Check
                                    type="checkbox"
                                    checked={fields.frequency.enabled}
                                    onChange={e => setEnabled('frequency', e.target.checked)}
                                    style={{ margin: 0 }}
                                />
                                <Form.Control
                                    type="text"
                                    size="sm"
                                    placeholder="Keep as is"
                                    value={fields.frequency.value}
                                    onChange={e => setFieldValue('frequency', e.target.value)}
                                    disabled={!fields.frequency.enabled}
                                    style={{ flex: 1 }}
                                />
                            </div>
                        </div>
                    )}

                    {minutesVisible && sectionHeader('Staffing Minutes')}
                    {minuteFields.map(f => numericRow(f.key, f.label))}

                    {costsVisible && sectionHeader('Additional Costs (R)')}
                    {COST_FIELDS.map(f => numericRow(f.key, f.label))}

                    {periodVisible && sectionHeader('Validity Period')}

                    {visible('date_from') && (
                        <div style={rowStyle}>
                            <span style={labelStyle}>Date From</span>
                            <div style={{ ...controlStyle, display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Form.Check type="checkbox" checked={fields.date_from.enabled} onChange={e => setEnabled('date_from', e.target.checked)} style={{ margin: 0 }} />
                                <Form.Control type="date" size="sm" value={fields.date_from.value} onChange={e => setFieldValue('date_from', e.target.value)} disabled={!fields.date_from.enabled} style={{ flex: 1 }} />
                            </div>
                        </div>
                    )}

                    {visible('date_to') && (
                        <div style={rowStyle}>
                            <span style={labelStyle}>Date To</span>
                            <div style={{ ...controlStyle, display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Form.Check type="checkbox" checked={fields.date_to.enabled} onChange={e => setEnabled('date_to', e.target.checked)} style={{ margin: 0 }} />
                                <Form.Control type="date" size="sm" value={fields.date_to.value} onChange={e => setFieldValue('date_to', e.target.value)} disabled={!fields.date_to.enabled} style={{ flex: 1 }} />
                            </div>
                        </div>
                    )}
                </div>

                {isApplying && (
                    <div style={{ padding: '12px 16px' }}>
                        <div className="d-flex justify-content-between mb-1" style={{ fontSize: '0.85rem', color: 'var(--theme-text-secondary)' }}>
                            <span>Applying changes...</span>
                            <span>{progress.done} / {progress.total}</span>
                        </div>
                        <ProgressBar now={progressPercent} variant="success" style={{ height: '6px' }} />
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose} disabled={isApplying}>Cancel</Button>
                <Button variant="primary" onClick={handleApply} disabled={isApplying}>
                    {isApplying ? 'Applying...' : 'Apply'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

const rowStyle = {
    display: 'flex',
    alignItems: 'center',
    padding: '10px 12px',
    borderBottom: '1px solid #dee2e6',
    gap: '12px',
    backgroundColor: 'var(--theme-surface)',
};

const labelStyle = {
    minWidth: '200px',
    fontSize: '0.9rem',
    color: 'var(--theme-text-secondary)',
    flexShrink: 0,
};

const controlStyle = {
    flex: 1,
};

export default BulkEditPriceItemsModal;
